import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { api, errorMessage } from '@/lib/client'
import { COINFLIP_MULTIPLIER, payoutFor } from '@/lib/games'
import { formatShort } from '@/lib/money'
import { play } from '@/lib/sounds'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/generals/button'
import { Arena, BetInput, GameLayout, Panel, StatBox, useStake } from '@/components/games/game-ui'
import { usePlay } from '@/components/games/use-play'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { DONUT, LOGO_FIRE_A } from '@/components/pixel/sprites'

type Side = 'fire' | 'donut'

type FlipResponse = {
    side: Side
    result: Side
    won: boolean
    payout: number
    balance: number
}

const FLIP_MS = 1_500

const SIDES: Array<{ id: Side, label: string, sprite: typeof DONUT }> = [
    { id: 'fire', label: 'Fire', sprite: LOGO_FIRE_A },
    { id: 'donut', label: 'Donut', sprite: DONUT }
]

const CoinflipGame: FC = (): ReactNode => {
    const { maintenance, setBalance } = useSession()
    const { canBet, toast } = usePlay()
    const stake = useStake()
    const [side, setSide] = useState<Side>('fire')
    const [rotation, setRotation] = useState(0)
    const [flipping, setFlipping] = useState(false)
    const [outcome, setOutcome] = useState<FlipResponse | null>(null)
    const timer = useRef(0)

    useEffect(() => () => window.clearTimeout(timer.current), [])

    const flip = async () => {
        if (flipping || !canBet(stake.amount)) return

        setFlipping(true)
        setOutcome(null)

        try {
            const data = await api<FlipResponse>('/api/games/coinflip', { body: { amount: stake.amount, side } })

            // Spin at least five full turns and land on the side the server picked.
            setRotation((current) => {
                const base = current - (current % 360) + 360 * 5

                return base + (data.result === 'donut' ? 180 : 0)
            })
            play('flip')

            timer.current = window.setTimeout(() => {
                setFlipping(false)
                setOutcome(data)
                setBalance(data.balance)
                play(data.won ? 'win' : 'lose')
            }, FLIP_MS)
        }
        catch (caught) {
            setFlipping(false)
            toast(errorMessage(caught), 'error')
        }
    }

    const payout = stake.amount ? payoutFor(stake.amount, COINFLIP_MULTIPLIER) : 0

    let status: ReactNode = 'Pick a side and flip'

    if (flipping) status = 'Flipping…'
    else if (outcome?.won) status = <span className='text-success'>It&apos;s {outcome.result === 'fire' ? 'Fire' : 'Donut'}! You won {formatShort(outcome.payout)}</span>
    else if (outcome) status = <span className='text-danger'>It&apos;s {outcome.result === 'fire' ? 'Fire' : 'Donut'}. Better luck next flip</span>

    const panel = (
        <Panel>
            <BetInput value={stake.text} onChange={stake.setText} disabled={flipping} />

            <div>
                <p id='coinflip-side' className='mb-1.5 text-sm font-semibold text-foreground/90'>Side</p>

                <div role='radiogroup' aria-labelledby='coinflip-side' className='grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface p-1'>
                    {SIDES.map((option) => (
                        <button
                            key={option.id}
                            type='button'
                            role='radio'
                            aria-checked={side === option.id}
                            disabled={flipping}
                            onClick={() => {
                                setSide(option.id)
                                play('click')
                            }}
                            className={cn(
                                'flex h-10 items-center justify-center gap-2 rounded-md text-[15px] font-bold transition',
                                side === option.id ? 'bg-secondary text-foreground shadow-[inset_0_0_0_1px_var(--color-primary)]' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <PixelSprite sprite={option.sprite} className='h-5 w-auto' />
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className='grid grid-cols-2 gap-2'>
                <StatBox label='Win chance' value='50%' />
                <StatBox label='Payout on win' value={stake.amount ? formatShort(payout) : '–'} />
            </div>

            <Button size='lg' className='w-full' onClick={flip} loading={flipping} disabled={maintenance}>
                {maintenance ? 'Paused for maintenance' : flipping ? 'Flipping' : 'Flip coin'}
            </Button>
        </Panel>
    )

    const arena = (
        <Arena className='flex flex-col items-center justify-center gap-7 px-6 py-10'>
            <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(255,180,60,0.12),transparent_55%)]' aria-hidden='true' />

            <div className='relative' style={{ perspective: '900px' }}>
                <div
                    className={cn('relative size-40 sm:size-48', flipping && 'scale-110', 'transition-[scale] duration-300')}
                    style={{
                        transformStyle: 'preserve-3d',
                        transform: `rotateY(${rotation}deg)`,
                        transition: flipping ? `transform ${FLIP_MS}ms cubic-bezier(0.18, 0.7, 0.18, 1), scale 300ms` : 'scale 300ms'
                    }}
                    role='img'
                    aria-label={outcome ? `Coin landed on ${outcome.result}` : 'Coin'}
                >
                    <div className='df-coin-face absolute inset-0 flex items-center justify-center rounded-full'>
                        <PixelSprite sprite={LOGO_FIRE_A} className='w-[50%]' />
                    </div>

                    <div className='df-coin-face absolute inset-0 flex items-center justify-center rounded-full' style={{ transform: 'rotateY(180deg)' }}>
                        <PixelSprite sprite={DONUT} className='w-[52%]' />
                    </div>
                </div>

                <div className={cn('absolute -bottom-6 left-1/2 h-3 w-28 -translate-x-1/2 rounded-full bg-black/50 blur-md transition-all duration-300', flipping && 'w-20 opacity-60')} aria-hidden='true' />
            </div>

            <p className='relative text-center text-lg font-bold sm:text-xl' aria-live='polite'>
                {status}
            </p>
        </Arena>
    )

    return <GameLayout panel={panel} arena={arena} />
}

export default CoinflipGame
